/**
 * Path → breadcrumb trail for the partner console.
 *
 * Lifted out of `components/layout/PageContainer.jsx`, where it used to live,
 * because the trail moved INTO the header (the CRM shows it there and the two
 * consoles are meant to read alike). Two places deriving it from the same
 * pathname would be two places to fix when a route is added, so there is one.
 *
 * The map covers the routes whose URL segment is not the label anyone would
 * write — `/commercials/markup` is "Markup & Fees", not "Markup". Anything not
 * listed falls back to the segment itself, title-cased, which is why a new
 * route renders sensibly before it is added here.
 *
 * A route with a sidebar entry (`config/navigation.jsx`) MUST use that exact
 * label — a breadcrumb reading differently from the menu item that led there
 * is its own bug, found live on `/communications` ("Communication Hub" vs the
 * menu's "Communications") and `/servicing` ("Servicing Queue" vs "Requests").
 * A route with no sidebar entry (the manual-booking forms, the orphaned
 * commercials mocks) is free to pick whatever reads best.
 */

export const BASE = '/partnerAdmin';

const LABELS = {
  '/': 'Dashboard',
  '/branding': 'Branding',
  '/commercials/markup': 'Markup & Fees',
  '/commercials/fees': 'Service Fees',
  '/commercials/promos': 'Promos & Vouchers',
  '/commercials/promo-codes': 'Promotions',
  '/operations/suppliers': 'Suppliers',
  '/operations/currency': 'Currency & FX',
  '/platform/users': 'Users & Roles',
  '/platform/wallet': 'Wallet & Credit',
  '/bookings': 'Bookings',
  '/bookings/manual-hotel': 'Manual Hotel Booking',
  '/bookings/manual-flight': 'Manual Flight Booking',
  '/customers': 'Customers',
  '/servicing': 'Requests',
  '/finance/transactions': 'Billing',
  '/profile': 'Profile',
  '/gst': 'GST',
  '/communications': 'Communications',
  '/reports/sales': 'Sales',
  '/reports/agents': 'Agents',
  '/reports/suppliers': 'Suppliers',
  '/reports/transactions': 'Transactions',
  '/travel/chat': 'Start Chat',
  '/travel/shop': 'Book',
  '/travel/history': 'Chat History',
  '/travel/emails': 'Email Queue',
};

const titleCase = (segment) =>
  segment.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * @param {string} pathname  the full location pathname
 * @returns {{ crumbs: Array<{label: string, to: string|null}>, title: string, parent: string }}
 *   `crumbs` always starts at Home; the last entry is never a link.
 *
 *   `title` is just the last crumb's own label — it feeds only the sticky
 *   header's short, secondary title line (`PartnerHeader.jsx`), never the
 *   screen's own self-description, which `PageContainer` renders in the page
 *   BODY from whatever `title`/`subtitle` the screen itself passes. Because of
 *   that split, a record id in the URL (a booking ref, a customer id) is a
 *   perfectly fine `title` here — it's genuinely the current position in the
 *   trail, and it renders once, not falling back to the parent's own label
 *   (which is already shown as the link right before it) and duplicating it.
 */
export function breadcrumbsFor(pathname) {
  const relative = pathname.startsWith(BASE) ? pathname.slice(BASE.length) || '/' : pathname;
  const segments = relative.split('/').filter(Boolean);

  const crumbs = [{ label: 'Home', to: BASE }];
  let title = 'Dashboard';
  let parent = '';

  segments.forEach((segment, index) => {
    const path = '/' + segments.slice(0, index + 1).join('/');
    const isLast = index === segments.length - 1;

    /*
     * An intermediate segment (`commercials` in `/commercials/markup`,
     * `platform` in `/platform/users`, `reports` in `/reports/sales`, …) is a
     * URL grouping prefix, not a page anyone can land on — there is no sidebar
     * item called "Commercials" to match it against. Rendering it anyway reads
     * as a crumb that names nothing real. Skip a non-last segment unless
     * LABELS names it explicitly as a page of its own (e.g. `/bookings`, which
     * IS a real list page and stays a clickable crumb on a booking's detail
     * route).
     */
    if (!isLast && !LABELS[path]) return;

    const label = LABELS[path] || titleCase(segment);
    crumbs.push({ label, to: isLast ? null : BASE + path });
    if (isLast) {
      title = label;
      parent = crumbs.length > 2 ? crumbs[crumbs.length - 2].label : '';
    }
  });

  return { crumbs, title, parent };
}

export default breadcrumbsFor;
